import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Save, Loader2 } from 'lucide-react';
import { db } from '@/lib/firebase/config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useToast } from '@/hooks/use-toast';

interface ContactInfo {
  phone: string;
  email: string;
  address: { ar: string; en: string };
  mapUrl: string;
}

export function ContactSection() {
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    phone: '',
    email: '',
    address: { ar: '', en: '' },
    mapUrl: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async () => {
    try {
      const docRef = doc(db, 'landing_contact', 'info');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setContactInfo({
          phone: data.phone || '',
          email: data.email || '',
          address: typeof data.address === 'string' ? { ar: data.address, en: '' } : (data.address || { ar: '', en: '' }),
          mapUrl: data.mapUrl || '',
        });
      }
    } catch (error) {
      console.error('Error fetching contact info:', error);
      toast({
        title: 'خطأ',
        description: 'حدث خطأ أثناء تحميل البيانات',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const docRef = doc(db, 'landing_contact', 'info');
      await setDoc(docRef, {
        ...contactInfo,
        updatedAt: new Date(),
      });

      toast({
        title: 'تم الحفظ',
        description: 'تم حفظ معلومات التواصل بنجاح',
      });
    } catch (error) {
      console.error('Error saving contact info:', error);
      toast({
        title: 'خطأ',
        description: 'حدث خطأ أثناء حفظ البيانات',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="text-center">جاري التحميل...</div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold">إدارة معلومات التواصل</h3>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              جاري الحفظ...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              حفظ التغييرات
            </>
          )}
        </Button>
      </div>

      <div className="space-y-6">
        {/* Phone */}
        <div className="space-y-2">
          <Label htmlFor="phone">رقم الهاتف</Label>
          <Input
            id="phone"
            value={contactInfo.phone}
            onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
            placeholder="966568403040+"
            dir="ltr"
          />
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email">البريد الإلكتروني</Label>
          <Input
            id="email"
            type="email"
            value={contactInfo.email}
            onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
            placeholder="info@namat.org.sa"
            dir="ltr"
          />
        </div>

        {/* Address */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">العنوان / Address</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="address-ar" className="text-sm text-muted-foreground">عربي</Label>
              <Textarea
                id="address-ar"
                value={contactInfo.address.ar}
                onChange={(e) => setContactInfo({ ...contactInfo, address: { ...contactInfo.address, ar: e.target.value } })}
                placeholder="حي الروضة، جدة، المملكة العربية السعودية"
                rows={3}
                dir="rtl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address-en" className="text-sm text-muted-foreground">English</Label>
              <Textarea
                id="address-en"
                value={contactInfo.address.en}
                onChange={(e) => setContactInfo({ ...contactInfo, address: { ...contactInfo.address, en: e.target.value } })}
                placeholder="Al Rawdah District, Jeddah, Saudi Arabia"
                rows={3}
                dir="ltr"
              />
            </div>
          </div>
        </div>

        {/* Map URL */}
        <div className="space-y-2">
          <Label htmlFor="mapUrl">رابط الخريطة (Google Maps Embed)</Label>
          <Textarea
            id="mapUrl"
            value={contactInfo.mapUrl}
            onChange={(e) => setContactInfo({ ...contactInfo, mapUrl: e.target.value })}
            placeholder="https://www.google.com/maps/embed?pb=..."
            rows={4}
            dir="ltr"
          />
          <p className="text-sm text-gray-500">
            للحصول على رابط الخريطة: انتقل إلى Google Maps، اختر الموقع، ثم اضغط على "مشاركة" ← "تضمين خريطة"
          </p>
        </div>

        {/* Preview */}
        {contactInfo.mapUrl && (
          <div className="space-y-2">
            <Label>معاينة الخريطة</Label>
            <div className="h-[300px] rounded-lg overflow-hidden border">
              <iframe
                src={contactInfo.mapUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
